import { useEffect, useRef, useState } from "react";
import {
  editarNota,
  lerCorrecoesDeNota,
  type CorrecaoDeNota,
  type NotaDoAdmin,
} from "../lib/adminPos";

/**
 * A secretaria corrige uma nota, com motivo obrigatório (000243).
 *
 * Pedido de Victor em 16/09/2026: *"Vamos criar o caminho para editar a nota."*
 * O item estava aberto desde 01/09, com a pergunta certa ao lado no runbook —
 * *"quem pode, com que registro"* —, e a resposta é esta tela mais a tabela
 * `pos_notas_editadas`.
 *
 * ── Modal, e não edição na linha ──────────────────────────────────────────
 *
 * A tabela mostra 25 linhas. Um campo e um botão em cada uma seriam 25 formulários
 * numa tela, que é a cicatriz do balcão ("Anexar e entregar" repetido 156 vezes
 * em 41.910px). E aqui há mais a dizer do que caberia na linha: o motivo é
 * obrigatório, e o HISTÓRICO de correções daquela tentativa é justamente o que se
 * quer ver antes de corrigir de novo.
 *
 * ── O que a tela mostra depois ────────────────────────────────────────────
 *
 * A resposta da RPC traz o ESTADO NOVO do aluno — a MAIOR nota dele na
 * disciplina e se ele passou —, e é isso que aparece. A pergunta de quem corrige
 * não é "gravou?", é "o aluno passou agora?": a aprovação é pela maior nota, e
 * corrigir uma tentativa para 6 não aprova quem tem outra com 4, nem reprova
 * quem tem outra com 9.
 */
export default function ModalCorrigirNota({
  linha,
  aberto,
  aoFechar,
  aoCorrigir,
}: {
  linha: NotaDoAdmin;
  aberto: boolean;
  aoFechar: () => void;
  /** A tabela relê: a nota mudou, e com ela o selo de aprovada. */
  aoCorrigir: (texto: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [nota, setNota] = useState(linha.nota !== null ? String(linha.nota) : "");
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [historico, setHistorico] = useState<CorrecaoDeNota[] | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (aberto && !dlg.open) dlg.showModal();
    if (!aberto && dlg.open) dlg.close();
  }, [aberto]);

  // O histórico é lido a cada abertura: entre duas, outra pessoa da equipe pode
  // ter corrigido a mesma nota, e é exatamente isso que quem abre precisa ver.
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    setErro(null);
    setMotivo("");
    setNota(linha.nota !== null ? String(linha.nota) : "");
    setHistorico(null);
    lerCorrecoesDeNota(linha.id).then((h) => {
      if (vivo) setHistorico(h);
    });
    return () => {
      vivo = false;
    };
  }, [aberto, linha.id, linha.nota]);

  // O número viaja, nunca o texto: `10,5` e `10.5` são o mesmo para quem digita
  // e coisas diferentes para o banco. É a regra da casa sobre máscara —
  // apresentação de um lado, número do outro, convertidos na FRONTEIRA.
  const valor = Number(nota.replace(",", "."));
  const valorValido = nota.trim() !== "" && Number.isFinite(valor) && valor >= 0 && valor <= 10;
  const podeSalvar = valorValido && motivo.trim() !== "" && !salvando;

  async function salvar() {
    setSalvando(true);
    setErro(null);
    const r = await editarNota(linha.id, valor, motivo);
    setSalvando(false);
    if (!r.ok) {
      setErro(r.detalhe ?? r.motivo);
      return;
    }
    if (!r.mudou) {
      // Não é erro, e a tela diz a verdade em vez de "corrigido".
      setErro("A nota já era esta. Nada foi alterado.");
      return;
    }
    aoCorrigir(r.detalhe ?? "Nota corrigida.");
    aoFechar();
  }

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      aria-labelledby="titulo-corrigir-nota"
      data-modal="corrigir-nota"
      // `m-auto` não é enfeite: o reset da casa zera a margem de tudo, e sem ele
      // o `inset: 0` do navegador resolve para o canto (a cicatriz do modal do
      // Mapa de Concursos, que abria encostado no alto à esquerda).
      className="m-auto max-h-[90vh] w-[min(94vw,32rem)] overflow-y-auto rounded-[1.125rem] bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-start justify-between gap-4 border-b border-borda p-6">
        <div>
          <h2 id="titulo-corrigir-nota" className="font-display text-xl font-bold text-titulo">
            Corrigir a nota
          </h2>
          <p className="mt-1 text-sm text-cinza">
            {linha.aluno_nome ?? linha.aluno_email} · {linha.disciplina} · {linha.numero}ª
            tentativa
          </p>
        </div>
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          className="grid size-11 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="size-5"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-6">
        <p className="text-sm text-cinza">
          Nota atual:{" "}
          <span data-nota-atual className="font-semibold text-tinta">
            {linha.nota !== null
              ? linha.nota.toLocaleString("pt-BR", { maximumFractionDigits: 1 })
              : "sem nota"}
          </span>
          {linha.acertos !== null && (
            <>
              {" "}
              · {linha.acertos} acerto{linha.acertos === 1 ? "" : "s"}
            </>
          )}
        </p>

        {/* Os acertos NÃO mudam, e a tela diz isso.
            `acertos` é o FATO da prova — quantas questões o aluno acertou —, e
            ajustá-lo para "fechar" com a nota nova fabricaria um fato para
            justificar um ato administrativo. Sem esta linha o operador supõe que
            os dois andam juntos e estranha o resultado. */}
        <p className="mt-1 text-xs text-cinza">
          A correção muda só a nota. Os acertos ficam como a prova registrou.
        </p>

        <label className="mt-5 block text-sm font-semibold text-titulo">
          Nota nova
          <input
            type="text"
            inputMode="decimal"
            value={nota}
            onChange={(e) => {
              setNota(e.target.value);
              setErro(null);
            }}
            data-campo="nota-nova"
            className="mt-1 w-28 rounded-lg border border-borda bg-fundo p-2 text-sm tabular-nums text-tinta focus:border-acento focus:outline-none"
          />
          <span className="ml-2 text-sm font-normal text-cinza">de 0 a 10</span>
        </label>
        {nota.trim() !== "" && !valorValido && (
          <p className="mt-1 text-sm text-erro-tinta">A nota vai de 0 a 10.</p>
        )}

        <label className="mt-4 block text-sm font-semibold text-titulo">
          Motivo
          <span className="font-normal text-cinza"> (obrigatório)</span>
          <input
            type="text"
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value);
              setErro(null);
            }}
            placeholder="Ex.: recurso deferido pela coordenação"
            data-campo="motivo-correcao"
            className="mt-1 w-full rounded-lg border border-borda bg-fundo p-2 text-sm text-tinta placeholder:text-cinza focus:border-acento focus:outline-none"
          />
        </label>
        <p className="mt-1 text-xs text-cinza">
          Fica registrado com o seu nome. Nota é o que decide o certificado, e a
          pergunta "quem mudou isto, e por quê" aparece meses depois.
        </p>

        <button
          onClick={salvar}
          disabled={!podeSalvar}
          data-acao="salvar-nota"
          // `pill-desligada` e não `opacity-*`: aqui o botão fica desligado
          // enquanto falta o motivo, ou seja o operador OLHA para ele tentando
          // entender. Transparência comunica espera; falta de permissão pede cor
          // própria (medido em 01/09: `opacity-50` num CTA dá 2,73 : 1).
          className={podeSalvar ? "pill-cta mt-5" : "pill-desligada mt-5"}
        >
          {salvando ? "Salvando…" : "Corrigir nota"}
        </button>

        {erro && (
          <p
            data-erro
            className="mt-4 rounded-lg border border-erro-tinta/40 bg-erro-fundo p-3 text-sm text-erro-tinta"
          >
            {erro}
          </p>
        )}

        {/* ── O que já foi corrigido nesta tentativa ──────────────────────── */}
        {historico !== null && historico.length > 0 && (
          <div className="mt-6 border-t border-borda pt-4">
            <p className="text-sm font-semibold text-titulo">Correções anteriores</p>
            <ul data-historico-correcoes className="mt-2 space-y-2">
              {historico.map((c) => (
                <li key={c.seq} className="text-sm">
                  <span className="text-cinza">
                    {new Date(c.criado_em).toLocaleDateString("pt-BR")}
                  </span>{" "}
                  <span className="tabular-nums text-tinta">
                    {c.nota_antes !== null
                      ? c.nota_antes.toLocaleString("pt-BR", { maximumFractionDigits: 1 })
                      : "—"}{" "}
                    →{" "}
                    {c.nota_depois.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                  </span>
                  {c.editado_por && <span className="text-cinza"> · {c.editado_por}</span>}
                  <span className="block text-xs text-cinza">{c.motivo}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </dialog>
  );
}
